import { Get } from 'config/apiServiceConfig'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import {
  GetSLAConfigResponseType,
  GetLocationListResponseType,
  GetLocationListRequestType,
  PutSLAConfigRequestType,
  PutSLAConfigResponseType,
  GetLocationDetailResponseType,
  GetLocationTypeResponseType,
  GetLocationDetailListType,
  GetLocationListByTypeResponseType,
  ClusterItemType,
  LocationByClusterItemType,
  GetKongLocationAllRequestType,
  GetKongLocationAllResponseType,
} from 'features/Location/@types/location'

const { Get: GetKong, Put: PutKong, Post: PostKong } = axiosInstanceApiGateway

export const getLocations = (params?: GetLocationListRequestType) =>
  Get<GetLocationListResponseType>({
    url: `/api/location`,
    params,
  })

export const getLocationList = ({ params }: { params: GetLocationListRequestType }) =>
  Get<GetLocationListResponseType>({
    url: `/api/location`,
    params: { pageSize: 99999, ...params },
  })

export const getLocationDetail = (id: number) =>
  Get<GetLocationDetailResponseType>({
    url: `/api/location/${id}`,
  })

export const getSLAConfig = () =>
  GetKong<GetSLAConfigResponseType>({ url: `/location/internal/v1/sla-config` })

export const putSLAConfig = ({ payload }: PutSLAConfigRequestType) =>
  PutKong<PutSLAConfigResponseType>({ url: `/location/internal/v1/sla-config`, data: payload })

export const getLocationTypeList = () =>
  Get<GetLocationTypeResponseType>({ url: '/api/location/type' })

export const getLocationDetailList = () =>
  Get<GetLocationDetailListType>({ url: '/api/location/detail' })

type GetLocationListByTypeParamsType = {
  name?: string
}

export const getLocationListByType = (
  locationType: string,
  params?: GetLocationListByTypeParamsType,
) =>
  Get<GetLocationListByTypeResponseType[]>({
    url: `/api/locations/${encodeURI(locationType)}`,
    params,
  })

export type GetAllLocationsResponseType = {
  location_id: number
  location_name: string
  location_type: string
}[]

export const getAllLocations = () => Get<GetAllLocationsResponseType>({ url: '/api/locations/all' })

export type GetKongLocationDetailRequestType = {
  id: string
}

export type GetKongLocationDetailResponseType = {
  detail: GetLocationDetailResponseType
}
export const getKongLocationDetail = ({ id }: GetKongLocationDetailRequestType) =>
  GetKong<GetKongLocationDetailResponseType>({ url: `location/internal/v1/locations/${id}/detail` })

export const getKongLocationAll = ({ params }: GetKongLocationAllRequestType) =>
  GetKong<GetKongLocationAllResponseType, false>({
    url: `location/internal/v1/locations`,
    params,
  })

type WhiteListType = {
  address: string
  type: number
}

export type LocationPayloadType = {
  location: {
    name: string
    address: string
    latitude: string
    longitude: string
    open_time: string
    close_time: string
    type: string
    active: boolean
    pic_3pl_id: number
    location_detail_id: number
    location_type_id: number
    location_flag_id: number
    area: [] // exclude, send empty array
    address_detail: 'dolor sed aliquip proident'
    spv: number[] // exclude, send empty array
    id: number
  }
  sla: number[]
  user_id: number
  schedule: {
    day: number
    open_time: string
    close_time: string
    is_24hr: boolean
    reroute: boolean
    close: boolean
    reason_id: number
    close_reason_title: string
    close_reason_content: string
    id: number
  }[]
  supervisor_id: number[]
  whitelist: WhiteListType[]
  blacklist: WhiteListType[]
  kitchen_schedule: {
    day: number
    open_time: string
    close_time: string
    is_24hr: boolean
    reroute: boolean
    close: boolean
    reason_id: number
    close_reason_title: string
    close_reason_content: string
    id: string
  }[]
  kitchen_sla: number[]
  cluster: {
    id: string
  }
}

export type PutKongLocationRequestType = {
  id: string
  payload: LocationPayloadType
}

export type GetClusterListResponseType = {
  data: {
    name: string
    id: string
    created_by: number
    updated_by: number
  }[]
  error: {
    status: boolean
    message: string
    code: number
  }
}

export const putKongLocation = ({ id, payload }: PutKongLocationRequestType) =>
  PutKong({ url: `location/internal/v1/location/${id}`, data: payload })

export type PostKongLocationRequestType = {
  payload: Pick<
    LocationPayloadType,
    | 'blacklist'
    | 'kitchen_schedule'
    | 'kitchen_sla'
    | 'schedule'
    | 'sla'
    | 'user_id'
    | 'supervisor_id'
    | 'whitelist'
  > & {
    location: {
      name: string
      address: string
      latitude: string
      longitude: string
      type: string
      active: boolean
      area: []
      spv: number[]
      open_time: string
      close_time: string
      pic_3pl_d: number
      location_detail_id: number
      location_type_id: number
      location_flag_id: number
      address_detail: string
    }
  }
}

export const postKongLocation = ({ payload }: PostKongLocationRequestType) =>
  PostKong({ url: `location/internal/v1/location`, data: payload })

export type PostVirtualMachineQRCodeRequestType = {
  payload: {
    location_id: number
    location_name: string
  }
}

export type PostVirtualMachineQRCodeResponseType = {
  data: {
    url: string
  }
  error: {
    code: number
    status: boolean
    message: string
  }
}

export const postVirtualMachineQRCode = ({ payload }: PostVirtualMachineQRCodeRequestType) =>
  PostKong<PostVirtualMachineQRCodeResponseType, true>({
    url: `location/internal/v1/locations/vm-code`,
    data: payload,
  })

export const getAllCluster = () =>
  axiosInstanceApiGateway.Get<ClusterItemType[]>({ url: `/location/internal/v1/cluster` })

export const getListLocationByClusterId = (id: string) =>
  axiosInstanceApiGateway.Get<{ location: LocationByClusterItemType[] }>({
    url: `/location/internal/v1/location-cluster/${id}`,
  })

export const getClusterList = () =>
  GetKong<GetClusterListResponseType, true>({ url: `/location/internal/v1/cluster` })

export type LocationOrderType = {
  is_upcoming_enable: boolean
  is_auto_pool: boolean
  location_name: string
  location_detail: string
  location_id: number
  is_auto_assign: boolean
  location_type: string
  is_new_ui: boolean
}

export type GetKongLocationOrdersResponseType = {
  data: LocationOrderType[]
}

export const getKongLocationOrders = () =>
  GetKong<GetKongLocationOrdersResponseType, true>({
    url: `/location/internal/v1/locations-order`,
  })

export type LocationsType = {
  location_id: number
  location_name: string
  location_type: string
}

export const getWarehouseLocationsAPI = () =>
  Get<LocationsType[]>({
    url: `/api/locations/warehouse`,
  })

export const getOrderLocationsAPI = () =>
  Get<{
    data: LocationsType[]
  }>({
    url: `/api/locations/order`,
  })

export type SLALongDeliveryType = {
  id: number
  minimum_duration: number
  maximum_duration: number
  total_duration: number
  close_time: string
  max_time_threshold: number
  display_name_home: string
  display_name_cart: string
  display_name_unit: string
  additional_sla_enable: boolean
  display_name: string
  open_time: string
  display_name_type: string
  name: string
  active: boolean
  available: boolean
  updated_by: number
  created_by: number
}

export type GetSLALongDeliveryResponseType = {
  sla: SLALongDeliveryType[]
}

export const getSLALongDelivery = () =>
  GetKong<GetSLALongDeliveryResponseType>({
    url: `/location/internal/v1/service-levels/long-delivery`,
  })
